/**
 * Finance — Close-cycle Automations + Close Health tile.
 * Extracted from FinanceClose.jsx in Iter 82.
 *
 * Exports:
 *  - <CloseHealthTile>       — snapshot card above tabs (readiness/last-run/lock)
 *  - <AutomationsTab>        — wraps AccrualSuggester + MonthlyScheduleCard
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, RefreshCw, Sparkles, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const fmt = (n) => {
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n || 0));
  } catch { return `₹${Number(n || 0).toFixed(2)}`; }
};

export function CloseHealthTile({ wsId }) {
  const [h, setH] = useState(null);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/finance/workspaces/${wsId}/close-cycle/health`).then((r) => setH(r.data)).catch(() => {});
  }, [wsId]);
  if (!h) return null;
  const score = h.last_month_readiness;
  const tone = score == null ? "text-muted-foreground" : score >= 80 ? "text-emerald-600" : score >= 50 ? "text-amber-600" : "text-rose-600";
  const daysAgo = h.days_since_last_run;
  const stale = daysAgo != null && daysAgo > 40;
  return (
    <Card className={`border ${stale ? "border-rose-500/40" : "border-primary/30"} bg-primary/[0.02]`} data-testid="close-health-tile">
      <CardContent className="p-4 flex flex-wrap items-center gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Close Health</div>
          <div className={`text-2xl font-mono ${tone}`} data-testid="ch-score">{score == null ? "—" : `${score}%`}</div>
          <div className="text-xs text-muted-foreground">Last month ({h.last_month}) readiness</div>
        </div>
        <div className="border-l pl-6">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Last run</div>
          <div className="text-sm" data-testid="ch-last-run">
            {h.last_run ? `${h.last_run.period} · ${daysAgo}d ago` : "Never"}
          </div>
        </div>
        <div className="border-l pl-6">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Locked through</div>
          <div className="text-sm font-mono" data-testid="ch-locked">{h.period_locked_through || "—"}</div>
        </div>
        {stale && (
          <div className="ml-auto text-xs text-rose-600 flex items-center gap-1" data-testid="ch-stale-warning">
            <AlertTriangle className="w-4 h-4" />No recent close activity — books are aging.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function AutomationsTab({ wsId }) {
  return (
    <div className="space-y-6" data-testid="fc-automations">
      <AccrualSuggester wsId={wsId} />
      <MonthlyScheduleCard wsId={wsId} />
    </div>
  );
}

function AccrualSuggester({ wsId }) {
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true); setData(null);
    try {
      const { data: d } = await api.post(`/finance/workspaces/${wsId}/close-cycle/ai-accruals`, { period });
      setData(d);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <Card><CardContent className="p-6 space-y-4" data-testid="accrual-panel">
      <div className="flex items-end gap-3">
        <div>
          <Label className="text-xs">Period</Label>
          <Input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="YYYY-MM" data-testid="accrual-period" />
        </div>
        <Button onClick={run} disabled={busy} data-testid="accrual-btn">
          {busy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {busy ? "Analyzing…" : "Suggest accruals"}
        </Button>
      </div>
      {data && (
        <div className="space-y-3" data-testid="accrual-result">
          <div className="text-sm border rounded p-3 bg-muted/20">
            <b>{data.count}</b> suggestions totalling <b className="font-mono">{fmt(data.total_amount)}</b>. {data.ai?.narrative}
          </div>
          {data.suggestions.length === 0 ? (
            <div className="text-sm text-muted-foreground">No recurring accrual patterns detected for {period}.</div>
          ) : (
            <div className="space-y-2">
              {data.suggestions.map((s, i) => (
                <div key={i} className="border rounded p-3 text-sm" data-testid={`accrual-item-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{s.vendor_name}</div>
                    <div className="flex items-center gap-2">
                      <Badge variant={s.confidence === "high" ? "default" : "outline"} className="text-[10px]">{s.confidence}</Badge>
                      <span className="font-mono">{fmt(s.avg_amount)}</span>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">{s.memo}</div>
                  {s.proposed_lines.length > 0 && (
                    <details className="mt-2 text-xs">
                      <summary className="cursor-pointer text-muted-foreground">Proposed journal lines</summary>
                      <pre className="mt-1 p-2 bg-muted/40 rounded overflow-x-auto text-[10px]">
                        {s.proposed_lines.map((l) => `${l.account_code} ${l.account_name}: Dr ${fmt(l.debit)} / Cr ${fmt(l.credit)}`).join("\n")}
                      </pre>
                    </details>
                  )}
                </div>
              ))}
              <div className="text-xs text-muted-foreground italic">Review these carefully before posting via <b>Journal Entries</b>. Reverse next month.</div>
            </div>
          )}
        </div>
      )}
    </CardContent></Card>
  );
}

function MonthlyScheduleCard({ wsId }) {
  const [cfg, setCfg] = useState(null);
  const [emails, setEmails] = useState("");
  useEffect(() => {
    if (!wsId) return;
    api.get(`/finance/workspaces/${wsId}/close-cycle/monthly-schedule`).then((r) => {
      setCfg(r.data); setEmails((r.data.notify_emails || []).join(", "));
    });
  }, [wsId]);
  if (!cfg) return null;
  const save = async () => {
    try {
      const body = {
        enabled: !!cfg.enabled,
        day_of_month: Number(cfg.day_of_month) || 5,
        hour_ist: Number(cfg.hour_ist ?? 9),
        notify_emails: emails.split(",").map((s) => s.trim()).filter(Boolean),
        auto_execute_safe_steps: !!cfg.auto_execute_safe_steps,
      };
      const { data } = await api.put(`/finance/workspaces/${wsId}/close-cycle/monthly-schedule`, body);
      setCfg(data); toast.success("Schedule saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };
  return (
    <Card><CardContent className="p-6 space-y-4" data-testid="schedule-panel">
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4" />
        <div className="font-medium">Scheduled AI monthly close</div>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: !!v })} data-testid="sched-enabled" />
        Enable — auto-generate an AI close plan for the just-ended month
      </label>
      <div className="grid grid-cols-2 gap-3 max-w-md">
        <div>
          <Label className="text-xs">Day of month</Label>
          <Input type="number" min="1" max="28" value={cfg.day_of_month} onChange={(e) => setCfg({ ...cfg, day_of_month: e.target.value })} data-testid="sched-day" />
        </div>
        <div>
          <Label className="text-xs">Hour (IST)</Label>
          <Input type="number" min="0" max="23" value={cfg.hour_ist} onChange={(e) => setCfg({ ...cfg, hour_ist: e.target.value })} data-testid="sched-hour" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Notify emails (comma-separated)</Label>
        <Input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="cfo@example.com, accounting@example.com" data-testid="sched-emails" />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={cfg.auto_execute_safe_steps} onCheckedChange={(v) => setCfg({ ...cfg, auto_execute_safe_steps: !!v })} data-testid="sched-autoexec" />
        Also auto-execute safe steps (depreciation, FX reval, auto-post invoices) — never locks the period
      </label>
      <Button onClick={save} data-testid="sched-save"><CheckCircle2 className="w-4 h-4 mr-2" />Save schedule</Button>
    </CardContent></Card>
  );
}
